import { assign, setup } from 'xstate';

import {
  AMBULATORY_BC,
  BARIATRIC_GURNEY_BC,
  D2D_BC,
  GURNEY_BC,
  MAX_HEIGHT,
  MAX_WEIGHT,
  NON_AMBULATORY_BC,
  WHEELCHAIR_BC,
} from '@/data/constants';
import {
  DEFAULT_SERVICE_LEVEL,
  PassengerPreference,
  PassengerPreferenceBC,
} from '@/data/models/passenger.model';

export type LevelOfCareContext = {
  section: 'none' | 'mobility' | 'assistance';
  focusIndex: number | null;
  transportationAttributeMap: Record<string, string> | null;
  reverseTransportationAttributeMap: Record<string, string> | null;
  /** UUID of the transportation attribute with passengerCareLevelBC === 'R2R' (mobility assistance). */
  r2rMobilityAttributeUuid: string | null;
  attributeLevel1Values: string[];
  attributeLevel2Values: string[];
  attributeLevel3Values: string[];
  attributeLevel4Values: string[];
  attributeLevel5Values: string[];
  attributeLevel6Values: string[];
  mobilityAttributeValues: string[];
  isNonAmbulatoryByIndex: boolean[];
  isGurneySelected: boolean[];
  isCareLevelMismatch: boolean[];
  exceedsMeasurementsLimitsByIndex: boolean[];
  withinMeasurementsLimitsByIndex: boolean[];
  passengerPreferences?: PassengerPreference[];
  memberPreferencesEnabled?: boolean;
};

export type LevelOfCareEvent =
  | { type: 'FOCUS_MOBILITY'; index: number }
  | { type: 'FOCUS_ASSISTANCE'; index: number }
  | { type: 'BLUR_MOBILITY'; index: number }
  | { type: 'BLUR_ASSISTANCE'; index: number }
  | {
      type: 'SET_TRANSPORTATION_ATTRIBUTE_MAP';
      map: Record<string, string> | null;
      reverseMap: Record<string, string> | null;
      r2rMobilityAttributeUuid: string | null;
      passengerPreferences?: PassengerPreference[];
      memberPreferencesEnabled?: boolean;
    }
  | {
      type: 'ATTRIBUTE_LEVEL_CHANGE';
      index: number;
      level: 1 | 2 | 3 | 4 | 5 | 6;
      value: string;
    }
  | {
      type: 'MEASUREMENTS_CHANGE';
      index: number;
      height: string;
      weight: string;
      heightTouched: boolean;
      weightTouched: boolean;
    }
  | { type: 'RESIZE'; count: number }
  | { type: 'RESET' }
  | { type: 'MOBILITY_ATTRIBUTE_CHANGE'; index: number; value: string };

function resizeStringArray(prev: string[], count: number): string[] {
  const c = Math.max(1, count);
  const next = prev.slice(0, c);
  while (next.length < c) next.push('');
  return next;
}

function updateAttributeLevelAt(
  prev: string[],
  index: number,
  value: string,
): string[] {
  const next = [...prev];
  while (next.length <= index) next.push('');
  next[index] = value;
  return next;
}

const ATTRIBUTE_LEVEL_VALUE_KEYS = [
  'attributeLevel2Values',
  'attributeLevel3Values',
  'attributeLevel4Values',
  'attributeLevel5Values',
  'attributeLevel6Values',
] as const;

type AttributeLevelValueKey = (typeof ATTRIBUTE_LEVEL_VALUE_KEYS)[number];

export function checkIsCareLevelMismatch({
  attributeLevel1,
  attributeLevel2,
  mobilityAttribute,
  reverseMap,
  passengerPreferences,
  passengerPreferencesEnabled,
}: {
  attributeLevel1: string;
  attributeLevel2: string;
  mobilityAttribute: string;
  reverseMap: Record<string, string> | null;
  passengerPreferences: PassengerPreference[];
  passengerPreferencesEnabled: boolean;
}) {
  if (!passengerPreferencesEnabled) return false;
  const passengerPreference = passengerPreferences.find(
    (pp) =>
      pp.passengerProfileAttributeTypeBC === PassengerPreferenceBC.serviceLevel,
  );
  if (!passengerPreferences.length) return false;

  const preferenceValue =
    passengerPreference?.attributeValue?.trim() || DEFAULT_SERVICE_LEVEL;

  // Passenger preferences are currently hardcoded to C2C, D2D, and WHEELCHAIR
  // Possible combinations are:
  // Preference: WHEELCHAIR - Must have Level 2 Wheelchair and D2D attributes
  // Preference: C2C - Must have No Level 2 attribute, a level 1 Ambulatory attribute and C2C Mobility Attribute
  // Preference: D2D - Must have No Level 2 attribute, a level 1 Ambulatory attribute and D2D Mobility Attribute
  switch (preferenceValue) {
    case 'WHEELCHAIR':
      if (!attributeLevel1) return false;
      const isWheelchairMatch =
        reverseMap?.[NON_AMBULATORY_BC] === attributeLevel1 &&
        reverseMap?.[WHEELCHAIR_BC] === attributeLevel2 &&
        reverseMap?.[D2D_BC] === mobilityAttribute;
      return !isWheelchairMatch;
    case 'C2C':
    case 'D2D':
      if (!attributeLevel1) return false;
      const isMatch =
        reverseMap?.[AMBULATORY_BC] === attributeLevel1 &&
        !attributeLevel2 &&
        reverseMap?.[preferenceValue] === mobilityAttribute;
      return !isMatch;
    default:
      return true;
  }
}

/** Clear attribute levels at `index` from `firstLevelToClear` through 6 (inclusive). */
function clearAttributeLevelsFrom(
  context: LevelOfCareContext,
  index: number,
  firstLevelToClear: 2 | 3 | 4 | 5 | 6,
): Pick<LevelOfCareContext, AttributeLevelValueKey> {
  const empty = '';
  const out = {} as Pick<LevelOfCareContext, AttributeLevelValueKey>;
  for (let i = 0; i < ATTRIBUTE_LEVEL_VALUE_KEYS.length; i++) {
    const key = ATTRIBUTE_LEVEL_VALUE_KEYS[i];
    const levelNum = i + 2;
    out[key] =
      levelNum >= firstLevelToClear
        ? updateAttributeLevelAt(context[key], index, empty)
        : context[key];
  }
  return out;
}

function computeNonAmbulatoryFlags(
  attributeLevel1Values: string[],
  map: Record<string, string> | null,
): boolean[] {
  return attributeLevel1Values.map((uuid) => {
    if (!uuid || !map) return false;
    const code = map[String(uuid)];
    return code !== undefined && code !== AMBULATORY_BC;
  });
}

function computeIsGurneySelected(
  attributeLevel2Values: string[],
  reverseMap: Record<string, string> | null,
): boolean[] {
  const gurneyUuid = reverseMap?.[GURNEY_BC];
  const bariatricGurneyUuid = reverseMap?.[BARIATRIC_GURNEY_BC];
  return attributeLevel2Values.map((uuid) => {
    if (!uuid) return false;
    return uuid === gurneyUuid || uuid === bariatricGurneyUuid;
  });
}

function isGurneyLevel2Uuid(
  uuid: string,
  reverseMap: Record<string, string> | null,
): boolean {
  if (!uuid || !reverseMap) return false;
  return (
    uuid === reverseMap[GURNEY_BC] || uuid === reverseMap[BARIATRIC_GURNEY_BC]
  );
}

/** When level 2 transitions onto gurney, default MobilityAttribute to R2R (TripLegForm parity). */
function mobilityR2rWhenEnteringGurney(
  prevMobility: string[],
  index: number,
  prevLevel2: string,
  nextLevel2: string,
  reverseMap: Record<string, string> | null,
  r2rUuid: string | null,
): string[] {
  if (!r2rUuid) return prevMobility;
  const wasGurney = isGurneyLevel2Uuid(prevLevel2, reverseMap);
  const nowGurney = isGurneyLevel2Uuid(nextLevel2, reverseMap);
  if (wasGurney || !nowGurney) return prevMobility;
  return updateAttributeLevelAt(prevMobility, index, r2rUuid);
}

function computeMeasurementLimitsFromStrings(
  height: string,
  weight: string,
): { exceedsMeasurementsLimits: boolean; withinMeasurementsLimits: boolean } {
  const heightNum = Number(height);
  const weightNum = Number(weight);
  return {
    exceedsMeasurementsLimits: heightNum > MAX_HEIGHT || weightNum > MAX_WEIGHT,
    withinMeasurementsLimits:
      heightNum <= MAX_HEIGHT && weightNum <= MAX_WEIGHT,
  };
}

/** Matches TripLegForm: only derive gurney type from measurements when both values are entered. */
function bothMeasurementsEntered(height: string, weight: string): boolean {
  const heightNum = Number(height);
  const weightNum = Number(weight);
  return Boolean(heightNum && weightNum);
}

function attributeLevel2FromMeasurementLimits(
  reverseMap: Record<string, string> | null,
  exceeds: boolean,
  within: boolean,
): string | null {
  if (!reverseMap) return null;
  if (exceeds) {
    const uuid = reverseMap[BARIATRIC_GURNEY_BC];
    return uuid ?? null;
  }
  if (within) {
    const uuid = reverseMap[GURNEY_BC];
    return uuid ?? null;
  }
  return null;
}

function resizeMeasurementLimitArrays(
  prevExceeds: boolean[],
  prevWithin: boolean[],
  count: number,
): { exceeds: boolean[]; within: boolean[] } {
  const c = Math.max(1, count);
  const exceeds = prevExceeds.slice(0, c);
  const within = prevWithin.slice(0, c);
  while (exceeds.length < c) {
    exceeds.push(false);
    within.push(false);
  }
  return { exceeds, within };
}

export const levelOfCareMachine = setup({
  types: {
    context: {} as LevelOfCareContext,
    events: {} as LevelOfCareEvent,
  },
}).createMachine({
  id: 'levelOfCare',
  context: {
    section: 'none',
    focusIndex: null,
    attributeLevel1Values: [''],
    attributeLevel2Values: [''],
    attributeLevel3Values: [''],
    attributeLevel4Values: [''],
    attributeLevel5Values: [''],
    attributeLevel6Values: [''],
    mobilityAttributeValues: [''],
    isNonAmbulatoryByIndex: [false],
    isGurneySelected: [false],
    transportationAttributeMap: null,
    reverseTransportationAttributeMap: null,
    r2rMobilityAttributeUuid: null,
    exceedsMeasurementsLimitsByIndex: [false],
    withinMeasurementsLimitsByIndex: [false],
    isCareLevelMismatch: [false],
    passengerPreferences: [],
    memberPreferencesEnabled: false,
  },
  initial: 'idle',
  states: {
    idle: {
      on: {
        FOCUS_MOBILITY: {
          target: 'mobilityFocused',
          actions: assign({
            section: () => 'mobility' as const,
            focusIndex: ({ event }) =>
              event.type === 'FOCUS_MOBILITY' ? event.index : null,
          }),
        },
        FOCUS_ASSISTANCE: {
          target: 'assistanceFocused',
          actions: assign({
            section: () => 'assistance' as const,
            focusIndex: ({ event }) =>
              event.type === 'FOCUS_ASSISTANCE' ? event.index : null,
          }),
        },
      },
    },
    mobilityFocused: {
      on: {
        FOCUS_MOBILITY: {
          actions: assign({
            focusIndex: ({ event }) =>
              event.type === 'FOCUS_MOBILITY' ? event.index : null,
          }),
        },
        FOCUS_ASSISTANCE: {
          target: 'assistanceFocused',
          actions: assign({
            section: () => 'assistance' as const,
            focusIndex: ({ event }) =>
              event.type === 'FOCUS_ASSISTANCE' ? event.index : null,
          }),
        },
        BLUR_MOBILITY: {
          target: 'idle',
          guard: ({ context, event }) =>
            event.type === 'BLUR_MOBILITY' &&
            context.focusIndex === event.index,
          actions: assign({ section: 'none', focusIndex: null }),
        },
      },
    },
    assistanceFocused: {
      on: {
        FOCUS_ASSISTANCE: {
          actions: assign({
            focusIndex: ({ event }) =>
              event.type === 'FOCUS_ASSISTANCE' ? event.index : null,
          }),
        },
        FOCUS_MOBILITY: {
          target: 'mobilityFocused',
          actions: assign({
            section: () => 'mobility' as const,
            focusIndex: ({ event }) =>
              event.type === 'FOCUS_MOBILITY' ? event.index : null,
          }),
        },
        BLUR_ASSISTANCE: {
          target: 'idle',
          guard: ({ context, event }) =>
            event.type === 'BLUR_ASSISTANCE' &&
            context.focusIndex === event.index,
          actions: assign({ section: 'none', focusIndex: null }),
        },
      },
    },
  },
  on: {
    SET_TRANSPORTATION_ATTRIBUTE_MAP: {
      actions: assign(({ context, event }) => {
        if (event.type !== 'SET_TRANSPORTATION_ATTRIBUTE_MAP') return {};
        const {
          map,
          reverseMap,
          r2rMobilityAttributeUuid,
          passengerPreferences,
          memberPreferencesEnabled,
        } = event;

        const isGurneyByIndex = computeIsGurneySelected(
          context.attributeLevel2Values,
          reverseMap,
        );
        let mobilityAttributeValues = context.mobilityAttributeValues;
        if (r2rMobilityAttributeUuid) {
          mobilityAttributeValues = context.mobilityAttributeValues.map(
            (m, i) => (isGurneyByIndex[i] && !m ? r2rMobilityAttributeUuid : m),
          );
        }

        return {
          transportationAttributeMap: map,
          reverseTransportationAttributeMap: reverseMap,
          r2rMobilityAttributeUuid,
          mobilityAttributeValues,
          isNonAmbulatoryByIndex: computeNonAmbulatoryFlags(
            context.attributeLevel1Values,
            map,
          ),
          isGurneySelected: isGurneyByIndex,
          passengerPreferences,
          memberPreferencesEnabled,
        };
      }),
    },
    ATTRIBUTE_LEVEL_CHANGE: {
      actions: assign(({ context, event }) => {
        if (event.type !== 'ATTRIBUTE_LEVEL_CHANGE') return {};
        const { index, level, value } = event;
        if (level === 1) {
          const next = [...context.attributeLevel1Values];
          while (next.length <= index) next.push('');
          next[index] = value;
          const cleared = clearAttributeLevelsFrom(context, index, 2);
          return {
            attributeLevel1Values: next,
            ...cleared,
            isNonAmbulatoryByIndex: computeNonAmbulatoryFlags(
              next,
              context.transportationAttributeMap,
            ),
            isGurneySelected: computeIsGurneySelected(
              cleared.attributeLevel2Values,
              context.reverseTransportationAttributeMap,
            ),
          };
        }
        if (level === 2) {
          const prevL2 = context.attributeLevel2Values[index] ?? '';
          const next = [...context.attributeLevel2Values];
          while (next.length <= index) next.push('');
          next[index] = value;
          const switchingBetweenGurneyTypes =
            isGurneyLevel2Uuid(
              prevL2,
              context.reverseTransportationAttributeMap,
            ) &&
            isGurneyLevel2Uuid(
              value,
              context.reverseTransportationAttributeMap,
            );
          const cleared = switchingBetweenGurneyTypes
            ? {
                attributeLevel2Values: context.attributeLevel2Values,
                attributeLevel3Values: updateAttributeLevelAt(
                  context.attributeLevel3Values,
                  index,
                  '',
                ),
                attributeLevel4Values: context.attributeLevel4Values,
                attributeLevel5Values: context.attributeLevel5Values,
                attributeLevel6Values: updateAttributeLevelAt(
                  context.attributeLevel6Values,
                  index,
                  '',
                ),
              }
            : clearAttributeLevelsFrom(context, index, 3);
          const mobilityAttributeValues = mobilityR2rWhenEnteringGurney(
            context.mobilityAttributeValues,
            index,
            prevL2,
            value,
            context.reverseTransportationAttributeMap,
            context.r2rMobilityAttributeUuid,
          );

          const isCareLevelMismatch = checkIsCareLevelMismatch({
            attributeLevel1: context.attributeLevel1Values[index] ?? '',
            attributeLevel2: next[index],
            mobilityAttribute: mobilityAttributeValues[index] ?? '',
            reverseMap: context.reverseTransportationAttributeMap,
            passengerPreferences: context.passengerPreferences ?? [],
            passengerPreferencesEnabled:
              context.memberPreferencesEnabled ?? false,
          });

          return {
            ...cleared,
            attributeLevel2Values: next,
            mobilityAttributeValues,
            isGurneySelected: computeIsGurneySelected(
              next,
              context.reverseTransportationAttributeMap,
            ),
            isCareLevelMismatch: [
              ...context.isCareLevelMismatch.slice(0, index),
              isCareLevelMismatch,
              ...context.isCareLevelMismatch.slice(index + 1),
            ],
          };
        }
        if (level === 3) {
          const nextL3 = updateAttributeLevelAt(
            context.attributeLevel3Values,
            index,
            value,
          );
          const cleared = clearAttributeLevelsFrom(context, index, 4);
          return {
            attributeLevel3Values: nextL3,
            attributeLevel4Values: cleared.attributeLevel4Values,
            attributeLevel5Values: cleared.attributeLevel5Values,
            attributeLevel6Values: cleared.attributeLevel6Values,
          };
        }
        if (level === 4) {
          const nextL4 = updateAttributeLevelAt(
            context.attributeLevel4Values,
            index,
            value,
          );
          const cleared = clearAttributeLevelsFrom(context, index, 5);
          return {
            attributeLevel4Values: nextL4,
            attributeLevel5Values: cleared.attributeLevel5Values,
            attributeLevel6Values: cleared.attributeLevel6Values,
          };
        }
        if (level === 5) {
          const nextL5 = updateAttributeLevelAt(
            context.attributeLevel5Values,
            index,
            value,
          );
          const cleared = clearAttributeLevelsFrom(context, index, 6);
          return {
            attributeLevel5Values: nextL5,
            attributeLevel6Values: cleared.attributeLevel6Values,
          };
        }
        if (level === 6) {
          return {
            attributeLevel6Values: updateAttributeLevelAt(
              context.attributeLevel6Values,
              index,
              value,
            ),
          };
        }
        return {};
      }),
    },
    MOBILITY_ATTRIBUTE_CHANGE: {
      actions: assign(({ context, event }) => {
        if (event.type !== 'MOBILITY_ATTRIBUTE_CHANGE') return {};
        const { index, value } = event;
        const isGurney =
          computeIsGurneySelected(
            context.attributeLevel2Values,
            context.reverseTransportationAttributeMap,
          )[index] ?? false;

        const isCareLevelMismatch = checkIsCareLevelMismatch({
          attributeLevel1: context.attributeLevel1Values[index] ?? '',
          attributeLevel2: context.attributeLevel2Values[index] ?? '',
          mobilityAttribute: value,
          reverseMap: context.reverseTransportationAttributeMap,
          passengerPreferences: context.passengerPreferences ?? [],
          passengerPreferencesEnabled:
            context.memberPreferencesEnabled ?? false,
        });
        // While gurney is selected the assistance control is disabled and defaults to R2R; ignore
        // stale non-R2R values from the form watch firing before setValue syncs the machine value.
        if (
          isGurney &&
          context.r2rMobilityAttributeUuid &&
          value !== context.r2rMobilityAttributeUuid
        ) {
          return {};
        }
        return {
          isCareLevelMismatch: [
            ...context.isCareLevelMismatch.slice(0, index),
            isCareLevelMismatch,
            ...context.isCareLevelMismatch.slice(index + 1),
          ],
          mobilityAttributeValues: updateAttributeLevelAt(
            context.mobilityAttributeValues,
            index,
            value,
          ),
        };
      }),
    },
    MEASUREMENTS_CHANGE: {
      actions: assign(({ context, event }) => {
        if (event.type !== 'MEASUREMENTS_CHANGE') return {};
        const { index, height, weight, heightTouched, weightTouched } = event;
        const shouldApplyMeasurementLimits = heightTouched && weightTouched;
        const limits = shouldApplyMeasurementLimits
          ? computeMeasurementLimitsFromStrings(height, weight)
          : {
              exceedsMeasurementsLimits: false,
              withinMeasurementsLimits: false,
            };
        const exceeds = [...context.exceedsMeasurementsLimitsByIndex];
        const within = [...context.withinMeasurementsLimitsByIndex];
        while (exceeds.length <= index) {
          exceeds.push(false);
          within.push(false);
        }
        exceeds[index] = limits.exceedsMeasurementsLimits;
        within[index] = limits.withinMeasurementsLimits;

        let attributeLevel2Values = context.attributeLevel2Values;
        let isGurneySelected = context.isGurneySelected;
        let mobilityAttributeValues = context.mobilityAttributeValues;
        let measurementClearedChildren:
          | ReturnType<typeof clearAttributeLevelsFrom>
          | undefined;
        if (
          shouldApplyMeasurementLimits &&
          bothMeasurementsEntered(height, weight)
        ) {
          const nextLevel2 = attributeLevel2FromMeasurementLimits(
            context.reverseTransportationAttributeMap,
            limits.exceedsMeasurementsLimits,
            limits.withinMeasurementsLimits,
          );
          if (nextLevel2 !== null) {
            const prevL2 = context.attributeLevel2Values[index] ?? '';
            attributeLevel2Values = updateAttributeLevelAt(
              context.attributeLevel2Values,
              index,
              nextLevel2,
            );
            isGurneySelected = computeIsGurneySelected(
              attributeLevel2Values,
              context.reverseTransportationAttributeMap,
            );
            mobilityAttributeValues = mobilityR2rWhenEnteringGurney(
              context.mobilityAttributeValues,
              index,
              prevL2,
              nextLevel2,
              context.reverseTransportationAttributeMap,
              context.r2rMobilityAttributeUuid,
            );
            if (attributeLevel2Values !== context.attributeLevel2Values) {
              measurementClearedChildren = clearAttributeLevelsFrom(
                context,
                index,
                3,
              );
            }
          }
        }

        return {
          exceedsMeasurementsLimitsByIndex: exceeds,
          withinMeasurementsLimitsByIndex: within,
          ...(measurementClearedChildren
            ? {
                ...measurementClearedChildren,
                attributeLevel2Values,
                mobilityAttributeValues,
                isGurneySelected,
              }
            : {}),
        };
      }),
    },
    RESIZE: {
      actions: assign(({ context, event }) => {
        if (event.type !== 'RESIZE') return {};
        const attributeLevel1Values = resizeStringArray(
          context.attributeLevel1Values,
          event.count,
        );
        const attributeLevel2Values = resizeStringArray(
          context.attributeLevel2Values,
          event.count,
        );
        const attributeLevel3Values = resizeStringArray(
          context.attributeLevel3Values,
          event.count,
        );
        const attributeLevel4Values = resizeStringArray(
          context.attributeLevel4Values,
          event.count,
        );
        const attributeLevel5Values = resizeStringArray(
          context.attributeLevel5Values,
          event.count,
        );
        const attributeLevel6Values = resizeStringArray(
          context.attributeLevel6Values,
          event.count,
        );
        const mobilityAttributeValues = resizeStringArray(
          context.mobilityAttributeValues,
          event.count,
        );
        const { exceeds, within } = resizeMeasurementLimitArrays(
          context.exceedsMeasurementsLimitsByIndex,
          context.withinMeasurementsLimitsByIndex,
          event.count,
        );
        return {
          attributeLevel1Values,
          attributeLevel2Values,
          attributeLevel3Values,
          attributeLevel4Values,
          attributeLevel5Values,
          attributeLevel6Values,
          mobilityAttributeValues,
          isNonAmbulatoryByIndex: computeNonAmbulatoryFlags(
            attributeLevel1Values,
            context.transportationAttributeMap,
          ),
          isGurneySelected: computeIsGurneySelected(
            attributeLevel2Values,
            context.reverseTransportationAttributeMap,
          ),
          exceedsMeasurementsLimitsByIndex: exceeds,
          withinMeasurementsLimitsByIndex: within,
        };
      }),
    },
    RESET: {
      target: '.idle',
      actions: assign({
        section: 'none',
        focusIndex: null,
        attributeLevel1Values: [''],
        attributeLevel2Values: [''],
        attributeLevel3Values: [''],
        attributeLevel4Values: [''],
        attributeLevel5Values: [''],
        attributeLevel6Values: [''],
        mobilityAttributeValues: [''],
        isNonAmbulatoryByIndex: [false],
        isGurneySelected: [false],
        exceedsMeasurementsLimitsByIndex: [false],
        withinMeasurementsLimitsByIndex: [false],
      }),
    },
  },
});
